// Whether a block has anything to show a VISITOR. One definition per
// block, read by every template that draws it and by the Ridgeline band
// that wraps it, so a block cannot vanish in one template and draw its
// heading over nothing in another.
//
// The rule (Mus, 2026-10-11): "always hide empty stuff for visitors, don't
// show placeholders." On the live page an empty block draws nothing at
// all, heading included, and an unfilled item inside a block (a card with
// no words, a slot with no photo) is left out. The editor canvas provides
// `tiqEditorCanvas` = true and keeps every placeholder, so a manager can
// see where content goes. harness/emptyblocks_verify.mjs renders every
// block emptied, in every template, and fails on anything drawn.
//
// No imports, so the harness can run it in node.

/** What a list of the org's own posts says to a visitor when it has none. */
export const NO_POSTS_YET = 'No posts yet. Check back soon.';

export const hasText = (v) => String(v ?? '').trim() !== '';

/** The features grid's cards that have something on them. */
export function featureItems(config, editing = false) {
    const all = Array.isArray(config?.features) ? config.features : [];
    return editing ? all : all.filter((f) => hasText(f?.title) || hasText(f?.desc) || hasText(f?.button?.label));
}

/** A call to action's buttons that have a label. */
export function ctaButtons(config) {
    return (Array.isArray(config?.buttons) ? config.buttons : []).filter((b) => hasText(b?.label));
}

/** A call to action says something: words, a button or a photo. */
export function ctaHasContent(config) {
    return hasText(config?.eyebrow) || hasText(config?.heading) || hasText(config?.subtitle)
        || hasText(config?.note) || hasText(config?.image) || ctaButtons(config).length > 0;
}

// Copy a new block ARRIVES with that is addressed to the person editing
// it — "Your headline goes here." — rather than to a visitor. blocks.js
// builds its defaults from these, so the list cannot drift from what a
// fresh block holds, and a visitor is shown none of it: a block left
// untouched reads as blank, not as a page somebody forgot to finish.
export const STARTER_TEXT = Object.freeze({
    headline: 'Your headline goes here.',
    featureBody: 'Describe this feature in a sentence or two — what it does, why it matters, who it helps.',
    heading: 'Your heading',
    paragraph: 'Your paragraph text here.',
    pageTitle: 'Page title',
});
const STARTER = new Set(Object.values(STARTER_TEXT));

// The two bullets a new Feature showcase arrives with. They read like
// real content, which is why they need naming: "Fast — quick to set up"
// on a club's live page is a claim nobody there made. A bullet still
// exactly as it arrived is starter copy; change either half and it is
// the club's.
export const STARTER_FEATURES = Object.freeze([
    Object.freeze({ icon: 'bolt', title: 'Fast', desc: 'Quick to set up and easy to maintain.' }),
    Object.freeze({ icon: 'check', title: 'Reliable', desc: 'Built on infrastructure you can trust.' }),
]);
const isStarterFeature = (f) => STARTER_FEATURES.some((s) => plain(f?.title) === s.title && plain(f?.desc) === s.desc);
const plain = (v) => String(v ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

/** Text a visitor may be shown: blank when it is still a block's starter copy. */
export function visitorText(v, editing = false) {
    if (editing) return v;
    return STARTER.has(plain(v)) ? '' : v;
}

/** Feature showcase bullets that say something. */
export function showcaseFeatures(config, editing = false) {
    const all = Array.isArray(config?.features) ? config.features : [];
    return editing ? all : all.filter((f) => (hasText(f?.title) || hasText(f?.desc)) && !isStarterFeature(f));
}

/** A feature showcase has something besides its eyebrow. */
export function showcaseHasContent(config) {
    const pills = (Array.isArray(config?.pills) ? config.pills : []).filter(hasText);
    return hasText(visitorText(config?.title)) || hasText(visitorText(config?.body))
        || pills.length > 0 || showcaseFeatures(config).length > 0
        || hasText(config?.cta_label) || hasText(config?.visual_image);
}

/** A text block's items that a visitor would see: no empty or starter ones. */
export function textItems(items, editing = false) {
    const all = Array.isArray(items) ? items : [];
    if (editing) return all;
    return all.filter((it) => {
        if (!it || typeof it !== 'object') return false;
        if (it.type === 'html') return hasText(it.html);
        return hasText(plain(visitorText(it.text))) || hasText(plain(visitorText(it.html)));
    });
}

/**
 * A live feed's empty line. The quiet day is news to a visitor, so the
 * line the owner set (or the default, on a block saved before the field
 * existed) is kept; an owner who cleared it gets no block on a quiet day.
 */
export function liveEmptyMessage(config, fallback) {
    const v = config?.empty_message;
    if (v === undefined || v === null) return fallback;
    return String(v).trim();
}

/** A phone mockup has something on its screen: a photo, a video or a chosen color. */
export function phoneHasScreen(mediaType, imageUrl, videoUrl) {
    const type = mediaType || 'image';
    if (type === 'color') return true;
    if (type === 'video') return hasText(videoUrl);
    if (type === 'image') return hasText(imageUrl);
    return false;
}

/** The phone a TrailsIQ hero draws for a visitor: switched on, with a screen. */
export function tiqHeroPhone(config) {
    return !!config?.show_phone
        && phoneHasScreen(config.phone_media_type, config.phone_image_url, config.phone_video_url);
}

/** A TrailsIQ hero says something: words, a labelled button or a phone with a screen. */
export function tiqHeroHasContent(config) {
    return hasText(config?.badge) || hasText(String(config?.title ?? '').replace(/[[\]]/g, ''))
        || hasText(config?.subtitle) || ctaButtons(config).length > 0 || tiqHeroPhone(config);
}

/** Which of a smartphone block's phones have a screen to show. */
export function smartphoneScreens(config) {
    return {
        first: phoneHasScreen(config?.media_type, config?.image_url, config?.video_url),
        second: !!config?.show_second
            && phoneHasScreen(config?.second_media_type, config?.second_image_url, config?.second_video_url),
    };
}

/** A page header's title as a visitor reads it: never the starter "Page title". */
export function pageHeaderTitle(config, editing = false) {
    return String(visitorText(config?.title ?? STARTER_TEXT.pageTitle, editing) ?? '');
}

/** The editor is showing the starter "Page title", not one somebody typed —
 *  drawn faded so it reads as a prompt rather than as the page's name. */
export function pageHeaderIsStarter(config) {
    return !hasText(plain(config?.title)) || plain(config?.title) === STARTER_TEXT.pageTitle;
}

/** The style a page header's title wears in the editor while it is still a prompt. */
export const starterTitleStyle = (config, editing) => (editing && pageHeaderIsStarter(config) ? { opacity: 0.45 } : null);

/** A page header says something: an eyebrow, a title, a line under it, a button or a photo. */
export function pageHeaderHasContent(config, eyebrow) {
    return hasText(eyebrow) || hasText(pageHeaderTitle(config).replace(/[[\]]/g, ''))
        || hasText(config?.subtitle) || hasText(config?.cta?.label) || hasText(config?.image);
}
